/**
 * Recovery Plan client service — Phase 2 (RJ-5 prerequisite)
 *
 * Wraps the two read-only server endpoints with a small in-memory cache so
 * that the Missões tab and any sibling surface (e.g. Bill chat goal context)
 * don't hammer the API when the user pages around.
 *
 * Cache TTLs follow the PRD §7.4 guidance:
 *   - today  : 5 minutes
 *   - history: 1 hour per (period, date) tuple
 *
 * Activity-write hooks elsewhere in the app should call
 * `recoveryPlanService.invalidateToday()` after the user completes a mission
 * (logged a sentiment, posted a community item, etc.) so the UI re-fetches
 * the live-derived row on next visit.
 *
 * Spec: documentation/11. Gamification/PRD_RECOVERY_JOURNEY.md (rev 3.1).
 */

import { apiGet } from './apiService';

// ---------------------------------------------------------------------------
// Types — mirror the server response shapes in services/recoveryPlanRoutes.js
// ---------------------------------------------------------------------------

export type UserFacingStageKey =
  | 'inicio'
  | 'fundacao'
  | 'desenvolvimento'
  | 'fortalecimento'
  | 'manutencao';

/**
 * Client-visible mission keys. The server continues to evaluate and retain
 * community actions for source data and history, but this context intentionally
 * exposes only the missions shown in the Missões page.
 */
export type MissionKey =
  | 'participar_encontro'
  | 'registrar_diario_sentimentos'
  | 'progredir_trilha';

export interface StageInfo {
  key: UserFacingStageKey;
  number: 1 | 2 | 3 | 4 | 5;
  internal: string;
  enteredAt: string | null;
}

export interface EncontroDay {
  date: string;       // YYYY-MM-DD
  dayNumber: number;  // 1..31
  weekday: number;    // 0=Sun..6=Sat
  checked: boolean;
}

export interface MissionStatus {
  key: MissionKey;
  completed: boolean;
}

export interface PendingTransition {
  fromStageNumber: number;
  fromStageKey: UserFacingStageKey;
  toStageNumber: number;
  toStageKey: UserFacingStageKey;
  enteredAt: string | null;
}

/**
 * Onboarding-defined goal token, e.g. 'identify_triggers'. The client maps
 * this to the existing `onboarding.goal.options.<camelCase>` translation
 * for display in the goal card. `null` for users whose onboarding completed
 * before app_goal was captured (legacy) or who skipped the step.
 *
 * Independent of mission generation — missions are stage-driven on the
 * server. The two are displayed side-by-side on Missões but never feed
 * into each other (per April 2026 product decision overriding PRD §7.4
 * "one goal per stage").
 */
export type AppGoalKey =
  | 'reduce_control'
  | 'full_abstinence'
  | 'maintain_sobriety'
  | 'identify_triggers'
  | 'healthy_habits'
  | 'rebuild_relationships'
  | 'monitor_progress';

export interface RecoveryTodayResponse {
  ok: true;
  stage: StageInfo;
  appGoal: AppGoalKey | null;
  sobrietyDays: number | null;
  encontros7d: EncontroDay[];
  missions: MissionStatus[];
  completedToday: number;
  pendingTransition: PendingTransition | null;
}

export interface HistoryDayEvents {
  /** True if any row in `relapses` exists for the user on that day. */
  hadRelapse?: boolean;
  /** Total community_support rows the user authored that day. */
  supportsCount?: number;
  /**
   * Profile of the post-author the user's MOST RECENT support that day went
   * to. Populated whenever `supportsCount >= 1`. The UI uses it for the
   * avatar in both the singleton copy ("{name} recebeu seu suporte") and
   * the count-only copy ("{count} pessoas receberam seu suporte").
   */
  latestSupportRecipient?: {
    name: string;
    avatarUrl?: string | null;
  };
  /** Total active community_posts the user authored that day. */
  postsCount?: number;
  /**
   * Full payload + author profile of the user's MOST RECENT post that day.
   * Populated whenever `postsCount >= 1`. The UI renders the testimony card
   * when `postsCount === 1` and uses `author` for the avatar in the
   * multi-post case.
   */
  latestPost?: {
    id: string;
    content: string;
    topics: string[];
    isAnonymous: boolean;
    createdAt: string;
    author: { name: string; avatarUrl?: string | null } | null;
  };
}

/**
 * Aggregate-period AI recap. Server returns this object on every
 * non-`day` history request. `text` is the user-facing copy (already
 * translated/built server-side); `totals` is the per-mission completion
 * count across the period and is used by the UI to detect "no data" so
 * the aggregate row can render as non-tappable.
 *
 * Phase 1 generator is deterministic and stub-voiced; RJ-9 swaps in
 * the Bill memory summarizer without changing this shape.
 */
export interface AiSummary {
  text: string;
  generatedBy?: string;
  totals?: Partial<Record<MissionKey, number>>;
}

export interface RecoveryHistoryResponse {
  ok: true;
  period: 'day' | 'week' | 'month' | 'quarter' | 'halfyear' | 'year';
  startDate: string;
  endDate: string;
  days: Array<
    {
      date: string;
      missions: Record<MissionKey, boolean>;
      completedCount: number;
    } & HistoryDayEvents
  >;
  aiSummary?: AiSummary | null;
}

// ---------------------------------------------------------------------------
// In-memory cache (mirrors the existing PLUS_CACHE_TTL pattern in apiService)
// ---------------------------------------------------------------------------

const TODAY_TTL_MS = 5 * 60 * 1000;     // 5 minutes
const HISTORY_TTL_MS = 60 * 60 * 1000;  // 1 hour

let todayCache: { data: RecoveryTodayResponse; ts: number } | null = null;
const historyCache = new Map<string, { data: RecoveryHistoryResponse; ts: number }>();

function historyKey(period: string, date?: string) {
  return `${period}:${date || 'now'}`;
}

/**
 * Community actions remain in the source tables and server evaluator, but are
 * intentionally hidden from the client-facing missions context.
 */
export const MISSION_KEYS: MissionKey[] = [
  'participar_encontro',
  'registrar_diario_sentimentos',
  'progredir_trilha',
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// `apiGet` returns an ApiResponse wrapper `{ success, data, status, error }`,
// NOT the bare server payload. Callers below MUST unwrap `res.data` and only
// then read the server's `ok` flag. (Forgetting this caused the Missões tab
// to show its error state on every load even though the server returned 200.)
export async function getToday(opts?: { force?: boolean }): Promise<RecoveryTodayResponse | { ok: false; error: string }> {
  if (!opts?.force && todayCache && Date.now() - todayCache.ts < TODAY_TTL_MS) {
    return todayCache.data;
  }
  const res = await apiGet<RecoveryTodayResponse>('/api/recovery-plan/today');
  if (res.success && res.data && (res.data as any).ok) {
    const rawPayload = res.data as RecoveryTodayResponse & {
      missions?: Array<{ key: string; completed: boolean }>;
    };
    const visibleMissions = (rawPayload.missions || []).filter(
      (mission): mission is MissionStatus => MISSION_KEYS.includes(mission.key as MissionKey)
    );
    const payload: RecoveryTodayResponse = {
      ...rawPayload,
      missions: visibleMissions,
      completedToday: visibleMissions.filter((mission) => mission.completed).length,
    };
    todayCache = { data: payload, ts: Date.now() };
    return payload;
  }
  return { ok: false, error: res.error || `http_${res.status || 0}` };
}

export async function getHistory(
  period: 'day' | 'week' | 'month' | 'quarter' | 'halfyear' | 'year',
  date?: string,
  opts?: { force?: boolean }
): Promise<RecoveryHistoryResponse | { ok: false; error: string }> {
  const key = historyKey(period, date);
  const cached = historyCache.get(key);
  if (!opts?.force && cached && Date.now() - cached.ts < HISTORY_TTL_MS) {
    return cached.data;
  }
  const qs = new URLSearchParams({ period });
  if (date) qs.set('date', date);
  const res = await apiGet<RecoveryHistoryResponse>(`/api/recovery-plan/history?${qs.toString()}`);
  if (res.success && res.data && (res.data as any).ok) {
    const rawPayload = res.data as RecoveryHistoryResponse;
    const payload: RecoveryHistoryResponse = {
      ...rawPayload,
      days: rawPayload.days.map((day) => {
        const visibleMissions = Object.fromEntries(
          MISSION_KEYS.map((key) => [key, !!day.missions[key]])
        ) as Record<MissionKey, boolean>;
        return {
          ...day,
          missions: visibleMissions,
          completedCount: MISSION_KEYS.filter((key) => visibleMissions[key]).length,
        };
      }),
    };
    historyCache.set(key, { data: payload, ts: Date.now() });
    return payload;
  }
  return { ok: false, error: res.error || `http_${res.status || 0}` };
}

/**
 * Drop the cached today payload. Call after the user takes an action that
 * could complete a mission (logged a sentiment, attended a meeting, etc.).
 */
export function invalidateToday(): void {
  todayCache = null;
}

/**
 * Drop every cached history entry. Call when the daily-mission cron likely
 * just ran (e.g. on app cold-start past midnight) or after a backdated edit.
 */
export function invalidateHistory(): void {
  historyCache.clear();
}

/**
 * Drop everything. Call on logout or account deletion.
 */
export function clearRecoveryPlanCache(): void {
  todayCache = null;
  historyCache.clear();
}

export const recoveryPlanService = {
  getToday,
  getHistory,
  invalidateToday,
  invalidateHistory,
  clearRecoveryPlanCache,
  MISSION_KEYS,
};

export default recoveryPlanService;
